import {
  CrmAdapter,
  TemplateDiagnostic,
  TemplateSegment,
} from "./emailThumbnail.types";

const FIELD_NAME_PATTERN =
  /^[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*$/;

export class TemplateParseError extends Error {
  readonly diagnostics: TemplateDiagnostic[];

  constructor(diagnostic: TemplateDiagnostic) {
    super(diagnostic.message);
    this.name = "TemplateParseError";
    this.diagnostics = [diagnostic];
    Object.setPrototypeOf(this, TemplateParseError.prototype);
  }
}

export class TemplateRenderError extends Error {
  readonly diagnostics: TemplateDiagnostic[];

  constructor(diagnostic: TemplateDiagnostic) {
    super(diagnostic.message);
    this.name = "TemplateRenderError";
    this.diagnostics = [diagnostic];
    Object.setPrototypeOf(this, TemplateRenderError.prototype);
  }
}

const appendText = (segments: TemplateSegment[], value: string): void => {
  if (value) {
    segments.push({ type: "text", value });
  }
};

export const parseTemplate = (template: string): TemplateSegment[] => {
  const segments: TemplateSegment[] = [];
  let cursor = 0;

  while (cursor < template.length) {
    const openingIndex = template.indexOf("{{", cursor);
    const closingIndex = template.indexOf("}}", cursor);

    if (
      closingIndex !== -1 &&
      (openingIndex === -1 || closingIndex < openingIndex)
    ) {
      throw new TemplateParseError({
        code: "unmatched-closing-delimiter",
        message: "Unmatched closing delimiter in template",
        index: closingIndex,
      });
    }

    if (openingIndex === -1) {
      appendText(segments, template.slice(cursor));
      break;
    }

    appendText(segments, template.slice(cursor, openingIndex));
    const fieldEndIndex = template.indexOf("}}", openingIndex + 2);

    if (fieldEndIndex === -1) {
      throw new TemplateParseError({
        code: "unmatched-opening-delimiter",
        message: "Unmatched opening delimiter in template",
        index: openingIndex,
      });
    }

    const field = template.slice(openingIndex + 2, fieldEndIndex);
    if (!FIELD_NAME_PATTERN.test(field)) {
      throw new TemplateParseError({
        code: "invalid-field-name",
        message: `Invalid template field name: ${field}`,
        index: openingIndex,
        field,
      });
    }

    segments.push({ type: "field", field });
    cursor = fieldEndIndex + 2;
  }

  return segments;
};

export const getTemplateFields = (template: string): string[] => {
  const fields = new Set<string>();

  parseTemplate(template).forEach((segment) => {
    if (segment.type === "field") {
      fields.add(segment.field);
    }
  });

  return Array.from(fields);
};

export const renderTemplate = (
  segments: TemplateSegment[],
  adapter: CrmAdapter,
  mappings: Record<string, string>
): string =>
  segments
    .map((segment) => {
      if (segment.type === "text") {
        return segment.value;
      }

      const token = mappings[segment.field];
      if (!token) {
        throw new TemplateRenderError({
          code: "unmapped-field",
          message: `No CRM token is mapped for template field: ${segment.field}`,
          field: segment.field,
        });
      }

      if (!adapter.isValidToken(token)) {
        throw new TemplateRenderError({
          code: "invalid-crm-token",
          message: `Invalid ${adapter.label} token for template field: ${segment.field}`,
          field: segment.field,
        });
      }

      return token;
    })
    .join("");
